import type { ReactNode } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { cn } from '@/lib/cn';

const TERMS = {
  DOC: { name: 'Day of culture', text: 'Days since stocking. The stocking day is day 1.' },
  ABW: { name: 'Average body weight', text: 'The average weight of one shrimp, in grams.' },
  FCR: { name: 'Feed conversion ratio', text: 'Feed given ÷ weight gained. Lower is better.' },
} as const;

export type TermKey = keyof typeof TERMS;

/** An abbreviation that explains itself on tap or click. */
export function Term({ term, children, className }: { term: TermKey; children?: ReactNode; className?: string }) {
  const { name, text } = TERMS[term];
  return (
    <Popover as="span" className="inline">
      <PopoverButton
        type="button"
        aria-label={`${term}: ${name}`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        className={cn(
          'cursor-help rounded-sm underline decoration-dotted decoration-gray-400 underline-offset-2',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
          className
        )}
      >
        {children ?? term}
      </PopoverButton>
      <PopoverPanel
        as="span"
        anchor="bottom start"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        className="z-[70] block w-64 rounded-lg bg-gray-900 px-3 py-2 text-left text-xs font-normal normal-case tracking-normal text-white shadow-lg [--anchor-gap:6px] [--anchor-padding:8px]"
      >
        <span className="block font-semibold">{name}</span>
        <span className="mt-0.5 block text-gray-200">{text}</span>
      </PopoverPanel>
    </Popover>
  );
}
